/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable } from 'type-fest';
import type { ResultItem } from '@/utils/file';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as SpreadsheetAttachmentsSearchFormInitialData,
  ValidData as SpreadsheetAttachmentsSearchFormValidData,
} from '@/components/searchForms/SpreadsheetAttachmentsSearchForm.vue';
import type { SpreadsheetJobBookingViewDataComposable } from '@/composables/data/useSpreadsheetJobBookingViewData';

import { computed, reactive, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import _cloneDeep from 'lodash/cloneDeep';

import useDownloadCsv from '@/composables/useDownloadCsv';
import * as spreadsheetTableHelpers from '@/helpers/tables/spreadsheet';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateSpreadsheetJobBookingViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<SpreadsheetJobBookingViewDataComposable>>>
  >;
}

interface DefSpreadsheetJobBookingViewState {
  selectedResultId: number;
}

const defPrivateSpreadsheetJobBookingViewState: DefPrivateSpreadsheetJobBookingViewState =
  {
    data: {
      isFetchingSpreadsheetJobBookingData: undefined,
      spreadsheetJobBookingData: undefined,
      spreadsheetJobBookingDataError: undefined,
      refreshSpreadsheetJobBookingData: undefined,
      reload: undefined,
    },
  };

const defSpreadsheetJobBookingViewState: DefSpreadsheetJobBookingViewState = {
  selectedResultId: NaN,
};

function privateSpreadsheetJobBookingViewStoreSetup(
  helpers: PiniaSetupStoreHelpers,
) {
  const state = reactive(
    _cloneDeep(defPrivateSpreadsheetJobBookingViewState),
  ) as DefPrivateSpreadsheetJobBookingViewState;
  const route = useRoute();

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    // const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateSpreadsheetJobBookingViewState));
    if (route.name === routeNames.SPREADSHEET_JOB_BOOKING) {
      // state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: SpreadsheetJobBookingViewDataComposable) {
    state.data = reactive(viewData);
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.SPREADSHEET_JOB_BOOKING) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
  };
}

function spreadsheetJobBookingViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defSpreadsheetJobBookingViewState));

  const _spreadsheetJobBookingViewStore =
    usePrivateSpreadsheetJobBookingViewStore();

  const data = computed(function () {
    return _spreadsheetJobBookingViewStore.data;
  });

  const spreadsheetJobBookingTableData = computed(function () {
    if (data.value.spreadsheetJobBookingDataError) return [];
    return spreadsheetTableHelpers.getTableData(
      data.value.spreadsheetJobBookingData?.booked,
    );
  });

  const initialData = computed<SpreadsheetAttachmentsSearchFormInitialData>(
    () => {
      return {};
    },
  );

  const { isDownloadingCSV, downloadCsv } = useDownloadCsv();

  const resultsForCsv = computed(function () {
    return spreadsheetTableHelpers.getTableData(
      data.value.spreadsheetJobBookingData?.booked,
    );
  });

  const jobIds = computed(() => {
    if (!data.value.spreadsheetJobBookingData?.booked) return [];
    return data.value.spreadsheetJobBookingData.booked.map((item) => {
      return item.rh_id;
    });
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defSpreadsheetJobBookingViewState));
    _spreadsheetJobBookingViewStore.$reset();
  }

  function init(viewData: SpreadsheetJobBookingViewDataComposable) {
    _spreadsheetJobBookingViewStore.setData(viewData);
  }

  async function search(
    formData: SpreadsheetAttachmentsSearchFormValidData,
    cb: () => void,
  ): Promise<void> {
    const file = formData.attachments[0];
    if (!data.value.refreshSpreadsheetJobBookingData) return;
    await data.value.refreshSpreadsheetJobBookingData(file);
    cb();
  }

  function downloadCSV() {
    downloadCsv(
      resultsForCsv.value as unknown as ResultItem[],
      'spreadsheetJobBooking',
    );
  }

  return {
    ...toRefs(state),
    data,
    spreadsheetJobBookingTableData,
    initialData,
    isDownloadingCSV,
    jobIds,
    $reset,
    init,
    search,
    downloadCSV,
  };
}

const usePrivateSpreadsheetJobBookingViewStore = defineStore(
  '_spreadsheetJobBookingView',
  privateSpreadsheetJobBookingViewStoreSetup,
);

export const useSpreadsheetJobBookingViewStore = defineStore(
  'spreadsheetJobBookingView',
  spreadsheetJobBookingViewStoreSetup,
);
